import * as React from "react"

import { setApiClient } from "@boundless/bl-api-client/client"
import * as Sentry from "@sentry/react"
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query"
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"
import {
  createBrowserHistory,
  createMemoryHistory,
  createRouter,
  RouterProvider,
} from "@tanstack/react-router"
import ReactDOM from "react-dom/client"
import { toast } from "sonner"

import { GenericErrorComponent } from "@/components/error/GenericErrorComponent"
import { NotFoundComponent } from "@/components/error/NotFoundComponent"
import { PendingComponent } from "@/components/error/PendingComponent"
import { AuthWrapper } from "@/components/router/AuthWrapper"
import { CompanyWrapper } from "@/components/router/CompanyWrapper"
import { PermissionWrapper } from "@/components/router/PermissionWrapper"
import { ThemeWrapper } from "@/components/router/ThemeWrapper"
import { blApiClient } from "@/lib/clients"
import { initializeMixpanel } from "@/lib/mixpanel-client"
import { registerScreenChangeEvents } from "@/lib/screen-change"
import { store } from "@/lib/store"
import {
  type BoundlessQueryMeta,
  isEmbed,
  isRateLimitedError,
  type QueryOrMutation,
  shouldDisplayToast,
  shouldRetryFactory,
} from "@/lib/utils"
import reportWebVitals from "@/reportWebVitals"
import { routeTree } from "@/routeTree.gen"

import "@/styles.css"

setApiClient(blApiClient)
initializeMixpanel()

// Defense-in-depth for the blank-screen-after-deploy failure: a client holding
// a stale index.html can request a content-hashed chunk a newer build no longer
// serves, and Vite fires `vite:preloadError` on the failed dynamic import. Reload
// to fetch a fresh index.html pointing at chunks that exist. A time-based guard
// prevents a tight reload loop (if a chunk is genuinely missing) while still
// recovering from a later, separate deploy in the same long-lived tab.
const PRELOAD_RELOAD_KEY = "vite-preload-reloaded-at"
const PRELOAD_RELOAD_COOLDOWN_MS = 10_000
window.addEventListener("vite:preloadError", () => {
  const last = Number(sessionStorage.getItem(PRELOAD_RELOAD_KEY) || 0)
  if (Date.now() - last < PRELOAD_RELOAD_COOLDOWN_MS) return
  sessionStorage.setItem(PRELOAD_RELOAD_KEY, String(Date.now()))
  window.location.reload()
})

declare module "@tanstack/react-query" {
  interface Register {
    queryMeta: BoundlessQueryMeta
    mutationMeta: BoundlessQueryMeta
  }
}

function showErrorToast(error: Error) {
  if (isRateLimitedError(error)) {
    // A fixed id collapses a burst of throttled requests into a single toast.
    toast.error("Too many requests", {
      id: "rate-limited",
      description: "Please wait a moment before trying again.",
    })
    return
  }
  toast.error("Something went wrong!", { description: error.message })
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      // Retrying a 429 keeps the client over the rate limit, extending the block.
      retry: shouldRetryFactory({
        skippedStatuses: [401, 403, 404, 409, 429],
      }),
    },
  },
  // Set Global Query and Mutation Callbacks to display a generic toast on
  // Server errors.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (shouldDisplayToast(error, query as QueryOrMutation)) {
        showErrorToast(error)
      }
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (shouldDisplayToast(error, mutation as QueryOrMutation)) {
        showErrorToast(error)
      }
    },
  }),
})

const history = isEmbed()
  ? createMemoryHistory({
      initialEntries: ["/embed"],
    })
  : createBrowserHistory()

export const router = createRouter({
  routeTree,
  context: {
    hasPermissions: store.getState().hasPermissions,
    queryClient,
    store: store,
  },
  defaultPreload: "intent",
  history,
  // Since we're using TanStack Query, we don't want loader calls to ever be
  // cached. This will ensure that the loader is always called when the route
  // is preloaded or visited
  defaultPreloadStaleTime: 0,
  defaultStructuralSharing: true,
  scrollRestoration: true,
  trailingSlash: "never",
  defaultNotFoundComponent: NotFoundComponent,
  defaultErrorComponent: GenericErrorComponent,
  defaultPendingComponent: PendingComponent,
})

registerScreenChangeEvents(router)

Sentry.init({
  dsn: import.meta.env.VITE_FRONTEND_SENTRY_DSN,
  integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
  tracesSampleRate: 1.0,
  sendDefaultPii: true,
})

export type BoundlessRouter = typeof router

// Necessary for the Router Type Safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
}

// Main App component

const rootElement = document.getElementById("app")
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement)
  root.render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <AuthWrapper>
          <ThemeWrapper>
            <PermissionWrapper>
              <CompanyWrapper>
                <RouterProvider router={router} />
              </CompanyWrapper>
            </PermissionWrapper>
          </ThemeWrapper>
        </AuthWrapper>
        <ReactQueryDevtools initialIsOpen={false} />
      </QueryClientProvider>
    </React.StrictMode>,
  )
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
